// ── 출퇴근 달력 메인 어셈블리 ────────────────────────────────────────────────
// 훅(useAttendanceCalendar)이 상태/핸들러를 담당하고,
// ControlBar(윗부분: 제목·버튼·탭 / 검색·필터 / 달 넘기기·범례·월별주별) / 달력 뷰 / 모달들을 조립해 렌더링한다.
// (FilterBar 는 2026-09-29 윗부분 통일로 없앴다 — 부서·상태는 CalendarFilterPopover, 이름 검색·범례는 ControlBar)
import React, { useEffect, useState } from 'react';
import { useAttendanceCalendar } from './useAttendanceCalendar';
import { ControlBar }        from './ControlBar';
import { CalendarFilterPopover } from './CalendarFilterPopover';
import { useAttendanceTools } from '../attendancemgmt/useAttendanceTools';
import AttendanceMenu         from '../attendancehub/AttendanceMenu';
import { useIpMenu }          from '../attendancehub/useIpMenu';
import { MonthView }          from './MonthView';
import { MobileMonthView }    from './MobileMonthView';
import { WeekView }           from './WeekView';
import { DayDetailModal }     from './DayDetailModal';
import { OverrideModal }      from './OverrideModal';
import { AttendanceEditModal, PlannedLocationModal } from './AttendanceModals';
import { useIsMobile } from '../../../hooks/useIsMobile';

/**
 * 관리자 출퇴근 달력 페이지 (어셈블리)
 *
 * 기능:
 *  - 월별 / 주별 달력 뷰 전환
 *  - 직원별 출퇴근 상태 시각화
 *  - 예외 스케줄 오버라이드 등록/수정/삭제
 *  - 관리자 출퇴근 기록 직접 수정
 *  - 공휴일 표시
 *  - 부서·이름·상태 필터
 *  - 인쇄 / 엑셀 다운로드
 */
// title/subtitle/tabSlot: 출퇴근 관리 탭 화면(attendancehub, 2026-09-29)에서 부를 때 제목·탭. 없으면 예전 그대로.
export default function AttendanceCalendar({
  calendarApiBase, companyCodes, selectedCompanyCodes, setSelectedCompanyCodes,
  title, subtitle, tabSlot = null,
  onPickPerson, // (선택) 이름 → 직원별 상세 탭(날짜 상세 창의 이름 · 휴대폰 날짜 목록의 이름). 탭 화면이 상세 탭을 열 수 있을 때만 준다.
  // (선택) [엑셀 다운로드][시간 설정] 설정 { attendancePath, companyCode, apiBasePath } — 상세 탭과 같은 기능.
  //   탭 화면이 상세 탭 열쇠(출퇴근 관리)가 있을 때만 준다 — 그 서버 기능이 그 열쇠를 보기 때문. 없으면 버튼 없음.
  tools = null,
  // (선택) 다른 탭(직원별 상세)에서 기록·예외 스케줄을 저장하면 숫자가 바뀐다 — 바뀌면 달력을 다시 불러온다.
  reloadSignal = 0,
  // (선택) ☰ 'IP 관리' 범위(2026-10-23) — 시간 설정·엑셀처럼 상세 탭 열쇠(tools)가 있을 때만 항목이 생긴다.
  ipScope = null,
} = {}) {
  const {
    // 뷰 상태
    year, month, viewMode, setViewMode,
    weekStart, setWeekStart,
    calData, loading,
    // 필터 상태
    filterDept,   setFilterDept,
    filterName,   setFilterName,
    filterStatus, setFilterStatus,
    // 파생값
    departments, filteredEmployees, filteredCells,
    // 모달 상태
    dayDetailModal,       setDayDetailModal,
    overrideModal,        setOverrideModal,
    attendanceEditModal,  setAttendanceEditModal,
    plannedLocationModal, setPlannedLocationModal,
    // 기타
    ipAliases,
    // 핸들러
    moveMonth, handleResetToToday,
    handleOverrideDelete, handleOverrideSave,
    handleAttendanceSave, handlePlannedLocationSave,
    handleDayClick, handleWeekCellClick,
    loadData,
  } = useAttendanceCalendar({ calendarApiBase, selectedCompanyCodes });
  useEffect(() => {
    if (reloadSignal) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadSignal]);
  const isMobile = useIsMobile();
  const toolsApi = useAttendanceTools(tools || {}); // 훅은 늘 부르고, 버튼·창은 tools 가 있을 때만 그린다
  const ipMenu = useIpMenu(tools ? ipScope : null, loadData); // 닫으면 달력(IP 이름표)도 다시 불러온다
  // 휴대폰 달력에서 고른 날 — 저장 뒤 다시 불러오는 동안 달력이 잠깐 사라져도 남게 여기서 든다(2026-09-29)
  const [mobilePickedYmd, setMobilePickedYmd] = useState(null);

  return (
    // 상단 여백은 표준 헤더(stdHeaderRow, ControlBar 내부)가 처리 — 2026-08-27 디자인 표준 통일
    // 바깥 틀은 직원별 상세 탭(attendancemgmt pageWrapStyle: grid·gap 10·좌우 여백 없음)과 같게 (2026-09-29 "탭 바꿀 때
    //   폼이 달라 왔다갔다"): 예전엔 PC 좌우 20px·글꼴 system-ui 라 탭을 바꾸면 제목이 20px 옆으로 가고 글꼴이 바뀌었다.
    <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'minmax(0, 1fr)', paddingBottom: 20, maxWidth: '100%' }}>
      {/* 인쇄 시 버튼·필터 숨김 스타일 */}
      <style>{`
        @media print {
          button:not(.print-keep), .no-print { display: none !important; }
          body { font-size: 11px; }
        }
      `}</style>

      {/* ── 윗부분 (2026-09-29 사용자 그림 — 직원별 상세 탭과 같은 틀) ──
          1줄 제목 … [달력|직원별 상세][☰] / 2줄 [이름 검색][필터(회사·부서·상태)] / 3줄 [월별|주별] … 달 넘기기 … 범례
          통합 메뉴 회사 선택은 제목 아래 칩이었다가 필터 창 안 "회사" 칸으로 (2026-09-30 사용자 ①) */}
      <ControlBar
        isMobile={isMobile}
        title={title}
        subtitle={subtitle}
        tabSlot={tabSlot}
        // ☰ 메뉴 — 직원별 상세 탭과 같은 항목·순서(사용자: 예외 스케줄 등록 → 시간 설정 → 엑셀 다운로드 → IP 관리).
        //   시간 설정·엑셀은 상세 탭과 같은 기능(달력에서 보던 달·이름 검색 기준), 상세 탭 열쇠가 있을 때만(tools).
        menu={(
          <AttendanceMenu items={[
            { key: 'override', label: '예외 스케줄 등록', onClick: () => setOverrideModal({}) },
            tools && { key: 'time', label: '시간 설정', onClick: toolsApi.openTimeSettings },
            tools && { key: 'excel', label: '엑셀 다운로드', onClick: () => toolsApi.exportExcel({ year, month, nameQuery: filterName }) },
            ipMenu.item,
          ]} />
        )}
        filterName={filterName}
        onFilterNameChange={setFilterName}
        filterButton={(
          <CalendarFilterPopover
            isMobile={isMobile}
            departments={departments}
            filterDept={filterDept} setFilterDept={setFilterDept}
            filterStatus={filterStatus} setFilterStatus={setFilterStatus}
            companyFilter={setSelectedCompanyCodes
              ? { companyCodes, selected: selectedCompanyCodes, onChange: setSelectedCompanyCodes } : null}
          />
        )}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        year={year}
        month={month}
        onMoveMonth={moveMonth}
        weekStart={weekStart}
        onSetWeekStart={setWeekStart}
        onResetToToday={handleResetToToday}
      />

      {/* ── 로딩 인디케이터 ── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
          달력 데이터를 불러오는 중...
        </div>
      )}

      {/* ── 달력 뷰 (월별 / 주별) ── */}
      {!loading && calData && (
        // 휴대폰 월별 = 점·인원 달력 + 날짜 목록 (2026-09-29, 7칸에 이름 칩을 욱여넣던 모양 대신)
        viewMode === 'month' && isMobile ? (
          <MobileMonthView
            calData={calData}
            year={year}
            month={month}
            filteredCells={filteredCells}
            onDayClick={handleDayClick}
            onPersonClick={onPickPerson}
            pickedYmd={mobilePickedYmd}
            onPickYmd={setMobilePickedYmd}
          />
        ) : viewMode === 'month' ? (
          // PC 월별: 칸 어디를 눌러도 날짜 상세 창 — 이름은 그 창 안에서 눌러 직원별 상세로(2026-10-01)
          <MonthView
            calData={calData}
            year={year}
            month={month}
            filteredCells={filteredCells}
            onDayClick={handleDayClick}
          />
        ) : (
          <WeekView
            calData={calData}
            weekStart={weekStart}
            filteredEmployees={filteredEmployees}
            onCellClick={handleWeekCellClick}
          />
        )
      )}

      {/* ── 데이터 없음 ── */}
      {!loading && !calData && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
          데이터를 불러올 수 없습니다. 새로고침해 주세요.
        </div>
      )}

      {/* ── 날짜 상세 모달 ── */}
      {dayDetailModal && (
        <DayDetailModal
          day={dayDetailModal}
          onClose={() => setDayDetailModal(null)}
          // 이름 → 직원별 상세(그 사람·그 달). 상세 탭 열쇠가 있을 때만(onPickPerson). 창은 닫고 넘어간다.
          onPickPerson={onPickPerson ? (userId, ymd) => { setDayDetailModal(null); onPickPerson(userId, ymd); } : undefined}
          onEditAttendance={(cell, date) => {
            // 일자 모달은 닫지 않고 수정 모달을 위에 띄움 (수정 후 일자 모달 자동 갱신)
            setAttendanceEditModal({ cell, date });
          }}
          onSetPlannedLocation={(cell, date) => {
            setDayDetailModal(null);
            setPlannedLocationModal({ cell, date });
          }}
          onAddOverride={(date) => {
            // 헤더의 "예외 등록" 버튼: 날짜만 지정, 직원은 폼에서 선택
            setDayDetailModal(null);
            setOverrideModal({ date_from: date, date_to: date });
          }}
          onAddOverrideForEmployee={(date, userId) => {
            // 직원별 "예외 등록" 버튼: 날짜 + 직원 pre-fill, 일자 모달은 열어둠
            setOverrideModal({ date_from: date, date_to: date, user_id: userId });
          }}
          onEditOverride={(cell) => {
            // 예외 배지 클릭 → 해당 예외 스케줄 수정 모달
            setOverrideModal({
              id:            cell.override_id,
              user_id:       cell.override_user_id,
              date_from:     cell.override_date_from,
              date_to:       cell.override_date_to,
              is_workday:    cell.is_workday,
              start_time:    cell.schedule_start,
              end_time:      cell.schedule_end,
              break_minutes: cell.override_break_minutes,
              work_location: cell.override_work_location,
              reason:        cell.override_reason,
            });
          }}
        />
      )}

      {/* ── 예외 스케줄 모달 ── */}
      {overrideModal !== null && (
        <OverrideModal
          modal={overrideModal}
          employees={calData?.employees || []}
          ipAliases={ipAliases}
          onClose={() => setOverrideModal(null)}
          onSave={handleOverrideSave}
          onDelete={handleOverrideDelete}
        />
      )}

      {/* ── 출퇴근 수정 모달 ── */}
      {attendanceEditModal && (
        <AttendanceEditModal
          cell={attendanceEditModal.cell}
          date={attendanceEditModal.date}
          ipAliases={ipAliases}
          onClose={() => setAttendanceEditModal(null)}
          onSave={handleAttendanceSave}
        />
      )}

      {/* ── 예정 근무지 설정 모달 ── */}
      {plannedLocationModal && (
        <PlannedLocationModal
          cell={plannedLocationModal.cell}
          date={plannedLocationModal.date}
          ipAliases={ipAliases}
          onClose={() => setPlannedLocationModal(null)}
          onSave={handlePlannedLocationSave}
        />
      )}

      {/* ── 시간 설정 창 (상세 탭과 같은 한 벌) ── */}
      {tools && toolsApi.modals}
      {ipMenu.modal}
    </div>
  );
}
